/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import Pagination from './Pagination';
import { Link } from 'react-router-dom';
import {
  calculateEndDate,
  formatNumber,
  getContributionDetailsByEffort,
  getDateHeader,
  isIncompletePeriod
} from '../../utils';
import Loading from '../Loading';
import { periods } from '../../constants';
import EffortLegend from './EffortLegend';
import { sendRequest } from '../../utils/dateHelpers';
import { useSelector } from 'react-redux';
import {
  getTableEmptyCellColor,
  getTableHeatColor
} from '../../utils/heatmapTheme';

const columnHelper = createColumnHelper();

const Table = ({
  type,
  loading,
  tableData,
  checkbox,
  dataKey,
  linkKey,
  customColumns,
  customColumnsHeader,
  widthShortColumns = [],
  hasLink = true,
  diffButton = true,
  firstColumnName,
  sorting = [],
  columnsWithCheckIcon = [],
  period,
  dateRange,
  formatNumberCell,
  customWidthClassName = 'w-32',
  searchFromTable = () => {},
  checkboxSelectFromTable = () => {},
  isCheckContributionHeatmap = false,
  showContributionTiers = true,
  filteredContent,
  keepMissingColumns = false
}) => {
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const tableScrollRef = useRef(null);
  const autoScrolledRef = useRef('');
  const heatmap = useState(true);
  const [displayContributionHeatmap, setDisplayContributionHeatmap] = useState(
    isCheckContributionHeatmap
  );

  const existsContributionHeatmap =
    showContributionTiers &&
    displayContributionHeatmap &&
    period === periods.MONTH &&
    linkKey === 'authors';
  const isDateGroupTable = type === 'dateGroup';

  const { userInfo = {} } = useSelector(state => state);
  const {
    user: { companyId: companyUid }
  } = userInfo;

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const handleRowSelection = rowId => {
    setSelectedRowIds(prev => {
      const newSelectedRowIds = prev.includes(rowId)
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId];

      checkboxSelectFromTable(newSelectedRowIds);

      return newSelectedRowIds;
    });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      searchFromTable(globalFilter);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [globalFilter]);

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter
    },
    sortingFns: {
      customSort: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);

        const isAInvalid = a === undefined || a === null;
        const isBInvalid = b === undefined || b === null;

        if (isAInvalid && isBInvalid) return 0;
        if (isAInvalid) return 1;
        if (isBInvalid) return -1;

        const isDesc = rowA.column?.getIsSorted?.() === 'desc';

        if (typeof a === 'number' && typeof b === 'number') {
          return isDesc ? b - a : a - b;
        }

        const parseTimestamp = value => {
          if (value === undefined || value === null || value === '')
            return null;
          const parsed = new Date(value).getTime();
          return Number.isNaN(parsed) ? null : parsed;
        };

        const dateA = parseTimestamp(a);
        const dateB = parseTimestamp(b);

        if (dateA !== null && dateB !== null) {
          if (dateA !== dateB) {
            return isDesc ? dateB - dateA : dateA - dateB;
          }

          const tieBreakerKeys = [
            'commit_timestamp',
            'commit_date',
            'committed_at',
            'created_at',
            'updated_at',
            'date',
            'day'
          ];

          for (const key of tieBreakerKeys) {
            const tieA = parseTimestamp(rowA.original?.[key]);
            const tieB = parseTimestamp(rowB.original?.[key]);
            if (tieA !== null && tieB !== null && tieA !== tieB) {
              return isDesc ? tieB - tieA : tieA - tieB;
            }
          }
        }

        const compareResult = String(a).localeCompare(String(b));
        return isDesc ? -compareResult : compareResult;
      }
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      sorting,
      pagination: {
        pageSize: 10
      }
    },
    globalFilterFn: (row, columnId, filterValue) => {
      return row.original[columnId]
        ?.toString()
        .toLowerCase()
        .includes(filterValue.toLowerCase());
    }
  });

  useEffect(() => {
    if (filteredContent) {
      filteredContent(
        table.getFilteredRowModel().rows.map(row => row.original)
      );
    }
  }, [table.getFilteredRowModel().rows]);

  useEffect(() => {
    if (
      !isDateGroupTable ||
      loading ||
      columns.length <= 2 ||
      data.length === 0
    ) {
      return;
    }

    const scrollKey = `${period}-${columns.length}-${data.length}-${dateRange?.startDate || ''}-${dateRange?.endDate || ''}`;

    if (autoScrolledRef.current === scrollKey) {
      return;
    }

    autoScrolledRef.current = scrollKey;

    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollContainer.scrollTo({
        left: scrollContainer.scrollWidth,
        behavior: 'smooth'
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    loading,
    isDateGroupTable,
    columns.length,
    data.length,
    period,
    dateRange
  ]);

  const fetchData = async id => {
    const url = '/table-detail/diff';

    const filterData = {
      id: id,
      companyUid: companyUid
    };

    const responseData = await sendRequest(url, 'POST', filterData);

    const commit_url = responseData?.commit_url || '';

    if (commit_url) {
      window.open(commit_url, '_blank');
    }

    return responseData.data;
  };

  const handleButtonClick = async row => {
    fetchData(row.id || row._id);
  };

  const simpleNormalize = () => {
    setData([...tableData]);
    let columnsTemp =
      customColumns ||
      (customColumnsHeader && Object.keys(customColumnsHeader)) ||
      Object.keys(tableData[0]);

    if (
      tableData[0] &&
      customColumnsHeader &&
      Object.keys(customColumnsHeader) &&
      tableData &&
      !keepMissingColumns &&
      !Object.keys(tableData[0])?.includes('git_effort') &&
      Object.keys(customColumnsHeader).includes('git_effort')
    ) {
      columnsTemp = columnsTemp.filter(item => item !== 'git_effort');
    }

    const columnsX = [];
    columnsTemp.forEach((date, index) => {
      if (index === 0 && diffButton) {
        columnsX.push(
          columnHelper.accessor(date, {
            header: 'Actions',
            sortUndefined: 'last',
            sortingFn: 'customSort',
            cell: ({ row }) => (
              <button
                className="flex w-full justify-center rounded bg-primary p-1 font-medium text-gray hover:bg-opacity-90  text-xs "
                onClick={() => handleButtonClick(row.original)}
              >
                Show Diff
              </button>
            )
          })
        );
      }

      columnsX.push(
        columnHelper.accessor(date, {
          header: customColumnsHeader?.[date]
            ? customColumnsHeader[date]
            : date,
          sortUndefined: 'last',
          sortingFn: 'customSort',
          cell: ({ getValue }) => {
            const value = getValue();
            if (columnsWithCheckIcon.includes(date)) {
              return value > 0 ? `✅` : '-';
            }
            if (formatNumberCell) {
              return formatNumber(value);
            }

            return value;
          }
        })
      );
    });

    setColumns(columnsX);
  };

  useEffect(() => {
    setGlobalFilter('');

    if (!tableData || tableData.length === 0) {
      setData([]);
      setColumns([]);
    }

    if (type === 'simple' && tableData.length > 0) {
      simpleNormalize();
    }

    if (type === 'dateGroup' && tableData) {
      const dates = Object.keys(tableData);
      const referenceDate = dateRange?.endDate || new Date();
      const incompleteDate =
        dates.length > 0 &&
        isIncompletePeriod(dates[dates.length - 1], period, referenceDate)
          ? dates[dates.length - 1]
          : null;

      const result = {};
      dates.forEach(date => {
        const entry = tableData[date];
        Object.keys(entry).forEach(key => {
          if (key !== 'date') {
            if (!result[key]) {
              result[key] = {};
            }
            result[key][date] = entry[key];
          }
        });
      });

      const formattedResult = Object.keys(result).map(name => {
        const tempName = dataKey?.[name]?.['label'];
        return { name: tempName, ID: name, ...result[name] };
      });

      let minValue = 0;
      let maxValue = 0;

      if (heatmap) {
        const allNumericValues = [];
        formattedResult.forEach(row => {
          Object.entries(row).forEach(([key, val]) => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(key) && typeof val === 'number') {
              allNumericValues.push(val);
            }
          });
        });

        minValue = Math.min(...allNumericValues);
        maxValue = Math.max(...allNumericValues);
      }

      const columnsX = [
        columnHelper.accessor('name', {
          header: firstColumnName || 'First Name',
          sortUndefined: 'last',
          sortingFn: 'customSort',
          cell: info => info.getValue()
        })
      ];

      dates.forEach(date => {
        columnsX.push(
          columnHelper.accessor(date, {
            header: getDateHeader(date, period),
            enableSorting: true,
            sortUndefined: 'last',
            sortingFn: 'customSort',
            meta: {
              isIncomplete: date === incompleteDate,
              endDate: calculateEndDate(date, period),
              backgroundColor: 'red',
              ...(heatmap && {
                backgroundColorFn: value =>
                  typeof value === 'number'
                    ? getTableHeatColor(value, minValue, maxValue, isDarkMode)
                    : getTableEmptyCellColor({ isDarkMode })
              })
            },
            cell: ({ getValue }) => {
              const value = getValue();
              if (columnsWithCheckIcon.includes(date)) {
                return value > 0 ? `✅` : '-';
              }
              if (value === null || value === undefined || value === '') {
                return '—';
              }
              if (formatNumberCell) {
                return formatNumber(value);
              }

              return value;
            }
          })
        );
      });

      const updatedData = formattedResult?.map(item => {
        const total = Object.entries(item)
          .filter(([key]) => /^\d{4}-\d{2}-\d{2}$/.test(key))
          .reduce((sum, [, value]) => sum + value, 0);

        return {
          ...item,
          total
        };
      });

      columnsX.push(
        columnHelper.accessor('total', {
          header: 'Total',
          sortUndefined: 'last',
          enableSorting: true,
          sortingFn: 'customSort',
          cell: info =>
            formatNumberCell ? formatNumber(info.getValue()) : info.getValue()
        })
      );

      setData([...updatedData]);
      setColumns([...columnsX]);
    }
  }, [dateRange, isDarkMode, period, tableData, type]);

  return (
    <div
      className={`col-span-12 flex w-full flex-col gap-4 rounded-sm border px-5 pb-5 pt-6 shadow-default sm:px-7.5 xl:col-span-12 ${
        isDarkMode ? 'border-strokedark bg-boxdark' : 'border-stroke bg-white'
      }`}
    >
      {loading ? (
        <Loading type="table" />
      ) : (
        <>
          <div className="flex w-full items-center gap-4">
            <input
              type="text"
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder="Search..."
              className={`w-48 rounded-lg border-[0.5px] bg-transparent px-3 py-2 font-normal outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter ${
                isDarkMode
                  ? 'border-strokedark bg-form-input text-white'
                  : 'border-gray-70 text-black'
              }`}
            />

            {type === 'dateGroup' &&
              period === periods.MONTH &&
              linkKey === 'authors' && (
                <div className="flex w-full flex-col gap-3">
                  {showContributionTiers && (
                    <label className="inline-flex items-center cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={displayContributionHeatmap}
                        onChange={e =>
                          setDisplayContributionHeatmap(e.target.checked)
                        }
                        className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800"
                      />
                      Show Contribution Tiers
                    </label>
                  )}

                  {existsContributionHeatmap && (
                    <div className="w-full">
                      <EffortLegend />
                    </div>
                  )}
                </div>
              )}
          </div>

          <div
            className={`flex flex-col overflow-hidden ${
              isDateGroupTable
                ? isDarkMode
                  ? 'border border-strokedark bg-boxdark'
                  : 'border border-slate-200/80 bg-white'
                : ''
            }`}
          >
            <div
              ref={isDateGroupTable ? tableScrollRef : null}
              className={`overflow-x-auto ${
                isDateGroupTable
                  ? isDarkMode
                    ? 'border-b border-strokedark scroll-smooth sm:rounded'
                    : 'border-b border-slate-200 scroll-smooth sm:rounded'
                  : 'border-b border-gray-200 dark:border-strokedark sm:rounded'
              }`}
            >
              <div className="min-w-full relative">
                <table className="w-full">
                  <thead
                    className={
                      isDateGroupTable
                        ? isDarkMode
                          ? 'divide-y divide-strokedark bg-meta-4'
                          : 'divide-y divide-slate-200 bg-slate-50/90'
                        : 'divide-y divide-gray-200 bg-gray-100 dark:divide-strokedark dark:bg-meta-4'
                    }
                  >
                    {table?.getHeaderGroups()?.map(headerGroup => (
                      <tr
                        key={headerGroup.id}
                        className={
                          isDateGroupTable
                            ? isDarkMode
                              ? 'divide-x divide-strokedark'
                              : 'divide-x divide-slate-300/80'
                            : 'divide-x divide-gray-200 dark:divide-strokedark'
                        }
                      >
                        {headerGroup.headers.map((header, index) => (
                          <th
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            style={{
                              backgroundColor: isDateGroupTable
                                ? header.column.columnDef.meta?.isIncomplete
                                  ? isDarkMode
                                    ? '#334155'
                                    : '#e5e7eb'
                                  : isDarkMode
                                    ? '#24303f'
                                    : '#e2e8f0'
                                : undefined,
                              borderRight:
                                isDateGroupTable && index === 0
                                  ? '1px solid rgba(148, 163, 184, 0.28)'
                                  : undefined
                            }}
                            className={`cursor-pointer px-2 py-3 text-xs font-medium uppercase tracking-wider ${
                              isDateGroupTable
                                ? `${index === 0 ? 'sticky left-0 z-20 text-left backdrop-blur-sm' : 'text-center'}`
                                : ''
                            } ${
                              header.column.columnDef.meta?.isIncomplete
                                ? isDarkMode
                                  ? 'text-slate-500'
                                  : 'text-slate-400'
                                : isDarkMode
                                  ? 'text-slate-300'
                                  : 'text-slate-500'
                            }`}
                          >
                            <div className={customWidthClassName}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽'
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody
                    className={
                      isDateGroupTable
                        ? isDarkMode
                          ? 'divide-y divide-strokedark bg-boxdark'
                          : 'divide-y divide-slate-200 bg-white'
                        : 'divide-y divide-gray-200 bg-white dark:divide-strokedark dark:bg-boxdark'
                    }
                  >
                    {table?.getRowModel()?.rows?.map(row => (
                      <tr
                        key={row.id}
                        className={
                          isDateGroupTable
                            ? isDarkMode
                              ? 'divide-x divide-strokedark transition-colors duration-200 hover:bg-white/5'
                              : 'divide-x divide-slate-200 transition-colors duration-200 hover:bg-slate-50/70'
                            : 'divide-x divide-gray-200'
                        }
                      >
                        {row.getVisibleCells().map((cell, index) => {
                          const firstColumnValue = cell?.row?.original?.ID;
                          const headerText = cell.column.columnDef.header;

                          let cellValue = flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          );

                          const endDate = cell.column.columnDef.meta?.endDate;
                          const isIncompleteColumn =
                            cell.column.columnDef.meta?.isIncomplete;

                          const bgColorFn =
                            cell.column.columnDef.meta?.backgroundColorFn ||
                            (() => 'transparent');

                          let backgroundColorTemp = bgColorFn(cell.getValue());
                          const isEmptyCellValue =
                            cell.getValue() === null ||
                            cell.getValue() === undefined ||
                            cell.getValue() === '';

                          if (
                            isDateGroupTable &&
                            index !== 0 &&
                            (backgroundColorTemp === 'transparent' ||
                              isEmptyCellValue)
                          ) {
                            backgroundColorTemp = getTableEmptyCellColor({
                              isDarkMode,
                              isIncompleteColumn
                            });
                          }

                          if (
                            existsContributionHeatmap &&
                            headerText !== 'Total'
                          ) {
                            const getContributionDetails =
                              getContributionDetailsByEffort(cell.getValue());
                            if (getContributionDetails) {
                              cellValue = getContributionDetails.label;
                              backgroundColorTemp = isDarkMode
                                ? getContributionDetails.hexDark ||
                                  getContributionDetails.hex
                                : getContributionDetails.hex;
                            }
                          }

                          const hasLinkExists = index === 0 || !hasLink;
                          let textColorClass = '';

                          if (isIncompleteColumn && index !== 0) {
                            textColorClass =
                              'text-gray-400 dark:text-slate-500';
                          } else if (isEmptyCellValue && index !== 0) {
                            textColorClass =
                              'text-slate-400 dark:text-slate-500';
                          } else if (!hasLinkExists) {
                            textColorClass = 'text-blue-500 dark:text-blue-400';
                          } else if (
                            existsContributionHeatmap &&
                            headerText !== 'Total'
                          ) {
                            const contributionDetails =
                              getContributionDetailsByEffort(cell.getValue());
                            if (contributionDetails) {
                              textColorClass = '';
                            }
                          }

                          return (
                            <td
                              key={cell.id}
                              className={`${
                                isDateGroupTable && index === 0
                                  ? 'sticky left-0 z-10 whitespace-nowrap px-2 py-2'
                                  : 'whitespace-nowrap px-2 py-2 text-center'
                              }`}
                              alt={cell.getValue()}
                              title={cell.getValue()}
                              style={{
                                backgroundColor: `${
                                  index === 0
                                    ? isDarkMode
                                      ? '#1e293b'
                                      : '#f8fafc'
                                    : isIncompleteColumn
                                      ? isDarkMode
                                        ? '#334155'
                                        : '#f3f4f6'
                                      : backgroundColorTemp
                                }`,
                                borderRight:
                                  isDateGroupTable && index === 0
                                    ? '1px solid rgba(148, 163, 184, 0.24)'
                                    : undefined
                              }}
                            >
                              <div
                                className={`w-full ${
                                  index === 0
                                    ? ''
                                    : 'flex items-center tems-center justify-center'
                                }`}
                                alt={cell.getValue()}
                                title={cell.getValue()}
                              >
                                <div
                                  className={`flex gap-2 ${widthShortColumns.includes(cell.column.id) ? 'min-w-0 w-64 ' : ''} 
                                    ${textColorClass}
                                    ${isDateGroupTable && index === 0 ? 'items-center bg-white px-1.5 py-1 dark:bg-meta-4' : 'text-xs leading-5 font-semibold dark:text-slate-200'}
                                  `}
                                  style={
                                    existsContributionHeatmap &&
                                    headerText !== 'Total'
                                      ? (() => {
                                          const contributionDetails =
                                            getContributionDetailsByEffort(
                                              cell.getValue()
                                            );

                                          if (!contributionDetails) return {};

                                          return {
                                            color: isDarkMode
                                              ? contributionDetails.textColorDark ||
                                                '#f8fafc'
                                              : contributionDetails.textColor ||
                                                '#0f172a'
                                          };
                                        })()
                                      : undefined
                                  }
                                >
                                  <span className="flex truncate gap-1 items-center">
                                    {checkbox && index === 0 && (
                                      <input
                                        type="checkbox"
                                        checked={selectedRowIds.includes(
                                          firstColumnValue
                                        )}
                                        onChange={() =>
                                          handleRowSelection(firstColumnValue)
                                        }
                                      />
                                    )}
                                    {hasLinkExists
                                      ? cellValue
                                      : (() => {
                                          const columnId =
                                            table?.getHeaderGroups?.()?.[0]
                                              ?.headers?.[index]?.id;

                                          const isTotalColumn =
                                            columnId === 'total';

                                          let urlTemp = `/commit-details?${linkKey}[0]=${firstColumnValue}&startDate=${columnId}&endDate=${endDate}`;

                                          if (isTotalColumn) {
                                            const tempDtaCalc =
                                              row.getVisibleCells();

                                            const minDate =
                                              tempDtaCalc[1].column.columnDef
                                                .meta?.endDate;

                                            const maxDate =
                                              tempDtaCalc[
                                                tempDtaCalc.length - 2
                                              ].column.columnDef.meta?.endDate;
                                            urlTemp = `/commit-details?${linkKey}[0]=${firstColumnValue}&startDate=${minDate}&endDate=${maxDate}`;
                                          }

                                          return (
                                            <Link target="_blank" to={urlTemp}>
                                              {cellValue}
                                            </Link>
                                          );
                                        })()}
                                  </span>
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <Pagination
            table={table}
            variant={isDateGroupTable ? 'dateGroup' : 'default'}
          />
        </>
      )}
    </div>
  );
};

export default Table;
